import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { useEffect, useState, useCallback, useRef } from "react";
import { Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, List, ListOrdered, Unlink, Heading2, Heading3 } from "lucide-react";

// Escape HTML entities for safe injection when converting plain text to <p> tags.
const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// Detect if a string already contains HTML markup (from prior RTE saves).
const looksLikeHtml = (s) => typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s);

// Convert legacy plain-text (with newlines) into paragraph HTML so TipTap can
// render each line as its own selectable/formattable paragraph.
const plainToHtml = (s) => {
  if (!s) return "";
  const lines = s.replace(/\r\n?/g, "\n").split(/\n{2,}|\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return "";
  return lines.map((l) => `<p>${escapeHtml(l)}</p>`).join("");
};

const normalizeInput = (value) => {
  if (!value || typeof value !== "string") return "";
  return looksLikeHtml(value) ? value : plainToHtml(value);
};

/**
 * Lightweight TipTap editor styled to match the Light Glassmorphism theme.
 * Emits sanitized HTML via onChange.
 *
 * Props:
 *  - value:      HTML string
 *  - onChange:   (html) => void
 *  - placeholder: string
 *  - minRows:   number of visible text rows (approx). default 3
 *  - testId:    data-testid prefix
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Start typing…", minRows = 3, testId = "rte" }) {
  const initialContent = normalizeInput(value);
  const wasLegacyPlain = useRef(!!value && !looksLikeHtml(value));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank", class: "vd-rte-link" },
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        class: "vd-rte-content focus:outline-none",
        style: `min-height:${Math.max(1, minRows) * 22 + 12}px`,
        "data-testid": `${testId}-content`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat truly empty editor as ""
      onChange && onChange(html === "<p></p>" ? "" : html);
    },
  });

  // If we normalized legacy plain text into HTML on mount, emit onChange once so
  // the parent form persists the cleaner HTML the next time it saves — even if
  // the user never touches the bio field. This prevents "invisible" plain-text
  // content that renders flat on the public page.
  useEffect(() => {
    if (!editor) return;
    if (wasLegacyPlain.current && initialContent && onChange) {
      onChange(initialContent);
      wasLegacyPlain.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Keep external value changes in sync (e.g., when loading data async)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = normalizeInput(value);
    if (next !== current && !(next === "" && current === "<p></p>")) {
      editor.commands.setContent(next || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const openLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    setLinkUrl(prev);
    setLinkOpen(true);
  }, [editor]);

  const applyLink = () => {
    if (!editor) return;
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const normalized = /^https?:\/\//i.test(url) || url.startsWith("mailto:") ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange("link").setLink({ href: normalized }).run();
    }
    setLinkOpen(false);
    setLinkUrl("");
  };

  const removeLink = () => editor?.chain().focus().unsetLink().run();

  if (!editor) return null;

  const isEmpty = editor.isEmpty;

  return (
    <div className="vd-rte" data-testid={testId}>
      <div className="vd-rte-toolbar" role="toolbar" aria-label="Formatting" data-testid={`${testId}-toolbar`}>
        <TBtn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading" testId={`${testId}-h2`}><Heading2 size={13} /></TBtn>
        <TBtn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="Subheading" testId={`${testId}-h3`}><Heading3 size={13} /></TBtn>
        <span className="vd-rte-sep" aria-hidden="true" />
        <TBtn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold" testId={`${testId}-bold`}><Bold size={13} /></TBtn>
        <TBtn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic" testId={`${testId}-italic`}><Italic size={13} /></TBtn>
        <TBtn active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Underline" testId={`${testId}-underline`}><UnderlineIcon size={13} /></TBtn>
        <span className="vd-rte-sep" aria-hidden="true" />
        <TBtn active={editor.isActive("link")} onClick={openLink} label="Link" testId={`${testId}-link`}><LinkIcon size={13} /></TBtn>
        {editor.isActive("link") && (
          <TBtn onClick={removeLink} label="Remove link" testId={`${testId}-unlink`}><Unlink size={13} /></TBtn>
        )}
        <span className="vd-rte-sep" aria-hidden="true" />
        <TBtn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list" testId={`${testId}-bullet`}><List size={13} /></TBtn>
        <TBtn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list" testId={`${testId}-ordered`}><ListOrdered size={13} /></TBtn>
      </div>

      <div className="vd-rte-body">
        {isEmpty && <div className="vd-rte-placeholder" aria-hidden="true">{placeholder}</div>}
        <EditorContent editor={editor} />
      </div>

      {linkOpen && (
        <div className="vd-rte-link-popover" data-testid={`${testId}-link-popover`}>
          <input
            autoFocus
            type="url"
            className="input-field text-sm flex-1"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } if (e.key === "Escape") setLinkOpen(false); }}
            data-testid={`${testId}-link-input`}
          />
          <button type="button" onClick={applyLink} className="btn-primary text-xs" data-testid={`${testId}-link-apply`}>Apply</button>
          <button type="button" onClick={() => setLinkOpen(false)} className="btn-ghost text-xs" data-testid={`${testId}-link-cancel`}>Cancel</button>
        </div>
      )}
    </div>
  );
}

function TBtn({ active, onClick, label, testId, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`vd-rte-btn ${active ? "is-active" : ""}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
